// NOTE: Migrated from @shopify/polaris React components to Polaris Web Components
// Removed: Text, InlineStack, BlockStack, Card, LegacyCard, Button, Banner, Box, IndexTable, useIndexResourceState, EmptySearchResult, Checkbox, TextField, Filters, useSetIndexFiltersMode, IndexFilters
// Migrated to: s-text, s-stack, s-section, s-button, s-banner, s-box, s-table, native state management, s-text-field, s-checkbox
import { useState, useEffect, useCallback } from 'react';
import { environment } from '../../Services/environment';
const endpoint = environment.serverUrl;

export default function BackupProductsTable({
  storeUrl,
  filePath,
  selectedRestoreProductIds,   // 👈 from parent
  onSelectionRestoreChange,    // 👈 callback to parent
  onBulkRestore,               // 👈 optional: for handling restore directly from table
}) {
  const queryString = window.location.search;
  
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Search/Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Removed useSetIndexFiltersMode - not needed for s-table
  
  // Pagination state
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // Bulk action states
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);
  const [bulkActionError, setBulkActionError] = useState(null);
  const [bulkActionSuccess, setBulkActionSuccess] = useState(null);
  
  // Upgrade notification state
  const [upgradeNotification, setUpgradeNotification] = useState(null);

  // Fetch products with pagination
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setUpgradeNotification(null);
    setBulkActionError(null);
    try {
      const res = await fetch(
        endpoint +
          `/api/Backup/view-products?storeUrl=${storeUrl}&filePath=${encodeURIComponent(filePath)}&pageNo=${pageNo}&pageSize=${pageSize}`
      );
      
      if (res.status === 403) {
        const errorData = await res.json();
        if (errorData.requiresUpgrade) {
          setUpgradeNotification(errorData.message);
          setProducts([]);
          setTotalCount(0);
          return;
        }
      }
      
      if (!res.ok) {
        throw new Error('Failed to load products');
      }
      
      const data = await res.json();
      
      console.log('Fetched products:', data.results);
      console.log('Sample product:', data.results?.[0]);
      
      setProducts(data.results || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);
      setHasPrevious(pageNo > 1);
      setHasNext(pageNo < data.totalPages);
    } catch (err) {
      console.error(err);
      setBulkActionError(err.message || 'Failed to load products');
      setProducts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [storeUrl, filePath, pageNo, pageSize]);

  // Reset to page 1 when filePath changes
  useEffect(() => {
    setPageNo(1);
  }, [filePath]);

  useEffect(() => {
    if (filePath) {
      fetchProducts();
    }
  }, [fetchProducts, filePath]);

  // Filter products based on search query
  useEffect(() => {
    console.log('Filter effect running - searchQuery:', searchQuery);
    console.log('Filter effect - products count:', products.length);
    
    if (!searchQuery.trim()) {
      console.log('No search query - showing all products');
      setFilteredProducts(products);
    } else {
      const query = searchQuery.toLowerCase();
      console.log('Filtering with query:', query);
      const filtered = products.filter(product => {
        const matchesTitle = product.title?.toLowerCase().includes(query);
        const matchesHandle = product.handle?.toLowerCase().includes(query);
        const matches = matchesTitle || matchesHandle;
        
        if (matches) {
          console.log('Match found:', product.title, product.handle);
        }
        
        return matches;
      });
      console.log('Filtered products count:', filtered.length);
      setFilteredProducts(filtered);
    }
  }, [products, searchQuery]);

  // Filters handlers
  const handleQueryValueRemove = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleQueryChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const handleClearAll = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Navigation to pricing page using App Bridge-compatible redirect
  const navigateToPricing = useCallback(() => {
    const pricingUrl = '/plans' + queryString;
    window.top.location.href = window.location.origin + pricingUrl;
  }, [queryString]);

  const resourceName = {
    singular: 'product',
    plural: 'products',
  };

  // Custom "Select All" state
  const [selectAll, setSelectAll] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);

  // Handle custom "Select All" checkbox
  const handleSelectAllChange = (checked) => {
    console.log('Select All changed to:', checked);
    setSelectAll(checked);
    
    if (checked) {
      // Select all across all pages
      setSelectedResources([]); // Empty array indicates "all"
    } else {
      // Deselect all
      setSelectedResources([]);
    }
  };

  // Handle individual row selection
  const handleRowSelectionChange = (id) => {
    console.log('=== handleRowSelectionChange ===');
    console.log('ID received:', id, 'Type:', typeof id);
    console.log('Current selectAll:', selectAll);
    console.log('Current selectedResources:', selectedResources);
    
    if (selectAll) {
      // If "select all" is active, disable it when individual selection happens
      console.log('Disabling selectAll');
      setSelectAll(false);
    }
    
    setSelectedResources((prev) => {
      console.log('Previous selectedResources:', prev);
      const isCurrentlySelected = prev.includes(id);
      console.log('Is currently selected:', isCurrentlySelected);
      
      const newSelection = isCurrentlySelected
        ? prev.filter((selectedId) => {
            console.log('Comparing:', selectedId, '(type:', typeof selectedId, ') with', id, '(type:', typeof id, ')');
            return selectedId !== id;
          })
        : [...prev, id];
      
      console.log('New selectedResources:', newSelection);
      return newSelection;
    });
  };

  // Calculate total selected count
  const getTotalSelectedCount = () => {
    if (selectAll) {
      return totalCount;
    }
    return selectedResources.length;
  };

  // Check if there are any selections
  const hasSelections = selectAll || selectedResources.length > 0;

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectAll(false);
    setSelectedResources([]);
  };

  // 🧠 Whenever selection changes → notify parent
  useEffect(() => {
    console.log('Selection changed - notifying parent');
    console.log('selectAll:', selectAll);
    console.log('selectedResources:', selectedResources);
    console.log('totalCount:', totalCount);
    
    if (selectAll) {
      // All resources selected across pages
      onSelectionRestoreChange([], true, totalCount); // Pass empty array, selectAll flag, and totalCount
    } else if (selectedResources.length > 0) {
      // Some resources selected
      onSelectionRestoreChange(selectedResources, false, selectedResources.length);
    } else {
      // Nothing selected - pass empty array with 0
      onSelectionRestoreChange([], false, 0);
    }
  }, [selectedResources, selectAll, totalCount, onSelectionRestoreChange]);

  const handlePrevious = () => {
    if (pageNo > 1) {
      setPageNo(pageNo - 1);
    }
  };

  const handleNext = () => {
    if (pageNo < totalPages) {
      setPageNo(pageNo + 1);
    }
  };

  // Handle bulk restore action
  const handleBulkRestore = async () => {
    console.log('=== Bulk Restore Action ===');
    console.log('storeUrl:', storeUrl);
    console.log('filePath:', filePath);
    console.log('selectAll:', selectAll);
    console.log('selectedResources:', selectedResources);
    console.log('totalCount:', totalCount);
    
    if (onBulkRestore) {
      // Use parent's handler if provided
      console.log('Using parent handler');
      onBulkRestore(selectedResources, selectAll, totalCount);
      return;
    }

    // Otherwise, handle it locally
    setIsBulkRestoring(true);
    setBulkActionError(null);
    setBulkActionSuccess(null);

    try {
      // Only set restoreAllProducts to true if selectAll is explicitly true
      // Don't use selectedResources.length === totalCount as a condition
      const restoreAllProducts = selectAll;

      const payload = {
        StoreUrl: storeUrl,          
        FilePath: filePath,          
        RestoreAllProducts: restoreAllProducts,  
        ProductIds: restoreAllProducts ? [] : selectedResources  
      };

      console.log('Payload being sent:', payload);

      const response = await fetch(endpoint + '/api/Backup/restore-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.message || 'Failed to restore products');
      }

      const result = await response.json();
      const itemsCount = restoreAllProducts ? totalCount : selectedResources.length;
      setBulkActionSuccess(`Successfully initiated restore of ${itemsCount} product(s). Bulk Operation ID: ${result.bulkOperationId}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setBulkActionSuccess(null);
      }, 5000);

    } catch (err) {
      console.error('Bulk restore error:', err);
      setBulkActionError(err.message || 'Failed to restore products');
    } finally {
      setIsBulkRestoring(false);
    }
  };

  // Promoted bulk actions
  const showBulkActions = selectAll || selectedResources.length > 0;

  const rowMarkup = filteredProducts.map(({ id, title, handle }, index) => {
    const isChecked = selectAll || selectedResources.includes(id);
    
    console.log('Rendering row - ID:', id, 'Type:', typeof id, 'isChecked:', isChecked);
    
    return (
      <s-table-row
        id={id}
        key={id}
      >
        <s-table-cell>
          <s-checkbox
            checked={isChecked ? true : undefined}
            onchange={(e) => {
              console.log('Checkbox changed:', e.target.checked, 'for ID:', id);
              handleRowSelectionChange(id);
            }}
          ></s-checkbox>
        </s-table-cell>
        <s-table-cell> 
          <s-clickable
            onClick={() => {
              handleRowSelectionChange(id);
            }}
          >
            <s-text variant="bodyMd" as="span" truncate>
              {title}
            </s-text>
          </s-clickable>
        </s-table-cell>
        <s-table-cell>
          <s-clickable
            onClick={() => {
              handleRowSelectionChange(id);
            }}
          >
            <s-text variant="bodyMd" as="span">
              {handle}
            </s-text>
          </s-clickable>
        </s-table-cell>
      </s-table-row>
    );
  });

  const loadingStateMarkup = (
    <s-table-row>
      <s-table-cell></s-table-cell>
      <s-table-cell>
          <s-stack alignItems="center" gap="base" padding="large">
          <s-spinner accessibilityLabel="Loading products" size="large" />
        </s-stack>
      </s-table-cell>
    </s-table-row>
  );

  const emptyStateMarkup = (
    <s-table-row>
      <s-table-cell colSpan="3">
        <s-box padding="extra-large">
          <s-stack direction="block" gap="large" alignment="center">
            <s-text variant="headingMd" as="h3" alignment="center">
              {searchQuery ? "No products found" : "No products found"}
            </s-text>
            <s-text variant="bodyMd" as="p" tone="subdued" alignment="center">
              {searchQuery 
                ? `No products match "${searchQuery}". Try a different search term.`
                : "There are no products in this backup file."}
            </s-text>
          </s-stack>
        </s-box>
      </s-table-cell>
    </s-table-row>
  );

  return (
    <>
      {upgradeNotification && (
        <s-banner heading="Upgrade Required" tone="warning" onDismiss={() => setUpgradeNotification(null)}>
          {upgradeNotification}
          <s-button slot="action" onClick={navigateToPricing}>View Pricing Plans</s-button>
        </s-banner>
      )}
      {bulkActionError && (
        <s-banner tone="critical" onDismiss={() => setBulkActionError(null)}>
          {bulkActionError}
        </s-banner>
      )}
       {bulkActionSuccess && (
        <s-banner heading={bulkActionSuccess} tone="success"></s-banner>
      )}
      <s-table
        loading={isLoading}
        paginate={totalPages > 1}
        hasPreviousPage={hasPrevious}
        hasNextPage={hasNext}
        onPreviousPage={handlePrevious}
        onNextPage={handleNext}
        padding="none"
      >
       
        <s-grid slot="filters" gridTemplateColumns="1fr auto">
            <s-text-field
              label="Search products"
              labelAccessibilityVisibility="exclusive"
              icon="search"
              placeholder="Search by title or handle..."
              value={searchQuery}
              clearable
              onInput={(e) => {
                const newValue = e.currentTarget?.value ?? '';
                setSearchQuery(newValue);
              }}
            />
            
            {/* Clear selection button next to search */}
            {hasSelections && (
              <s-button 
                variant="secondary"
                onClick={handleClearSelection}
              >
                Clear selection
              </s-button>
            )}
            {hasSelections && (
          <s-grid gridTemplateColumns="1fr auto" gap="base" block-alignment="center">
            <s-grid-item>
              <s-checkbox
                checked={selectAll ? true : undefined}
                label={selectAll
                  ? `All ${totalCount} products selected`
                  : `${selectedResources.length} product${selectedResources.length !== 1 ? 's' : ''} selected`}
                onchange={(e) => {
                  console.log('Select All checkbox changed:', e.target.checked);
                  handleSelectAllChange(e.target.checked);
                }}
              />
            </s-grid-item>
          </s-grid>
      )}
          </s-grid>
          
        <s-table-header-row>
          <s-table-header></s-table-header>
          <s-table-header>Title</s-table-header>
          <s-table-header>Handle</s-table-header>
        </s-table-header-row>
        <s-table-body>
          {isLoading 
              ? loadingStateMarkup 
              : filteredProducts.length === 0 
                ? emptyStateMarkup 
                : rowMarkup}
        </s-table-body>
      </s-table>
    </>
  );
}
